import { use, useCallback, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '@components/amplifyProvider';
import { datadogRum } from '@datadog/browser-rum';
import { envVariables } from '@fabric-ui/utils';
import {
  MutationOptions,
  QueryFunction,
  QueryKey,
  QueryObserverOptions,
  useInfiniteQuery,
  useMutation,
  UseMutationResult,
  useQueries,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { getAccountTokenFromLS, shouldSkipUILogin } from '@utils/auth.utils';
import { useNavigate } from '@utils/AvRouter';
import {
  coerceVariablesWithTypes,
  convertToGraphQLError,
  extractVariableTypes,
  getOperationName,
  safeDocumentParse,
} from '@utils/graphqlUtils';
import { AuthStorageKeys } from '@utils/localStorageAuthUtils.utils';
import { getAppIdFromPathName, getPathNameByAccount } from '@utils/router.utils';
import { getNetworkErrorCode } from '@utils/utils';
import { DocumentNode, FieldNode, GraphQLError } from 'graphql';
import { ClientError, GraphQLClient, GraphQLResponse, RequestDocument, Variables } from 'graphql-request';
import { print } from 'graphql/language/printer';
import { visit } from 'graphql/language/visitor';
import { useAuthContext } from '../context/AuthContextProvider';
import { GraphQLClientContext } from '../context/GraphQLClientProvider';
import { AuthContextType } from '../context/types';
import useHandleError from '../hooks/useHandleError';
import { APP_PATHS, FeatureFlags } from '../types';
import { ErrorTypes } from '../types/query.types';

export type UseGqlQueryOptions<TQueryFnData = unknown, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey> = Omit<
  UseQueryOptions<TQueryFnData, GraphQLError, TData, TQueryKey>,
  'queryFn' | 'queryKey' | 'select' | 'onSuccess' | 'onError'
> & {
  queryKey: TQueryKey;
  query: RequestDocument;
  variables?: Variables | (() => Variables);
  onSuccess?: (res: TData) => void;
  onError?: (res: GraphQLError) => void;
  select?: (data: TQueryFnData, baseResponse: TQueryFnData) => TData;
};

export const sanitizeQueryForUndefinedFields = (
  doc: DocumentNode,
  error: ClientError
): { sanitizedDoc: DocumentNode; removedFields: string[] } | null => {
  if (!error.response?.errors?.length) {
    return null;
  }

  const removedFields = error.response.errors.reduce((fields: string[], gqlError) => {
    const fieldUndefinedMatch = gqlError.message.match(/Field '([^']*)' in type '([^']*)' is undefined/);
    if (gqlError.message.includes('FieldUndefined') && fieldUndefinedMatch) {
      const fieldName = fieldUndefinedMatch[1];
      if (!fields.includes(fieldName)) {
        return [...fields, fieldName];
      }
    }
    return fields;
  }, []);

  if (removedFields.length === 0) {
    return null;
  }

  console.error(`${ErrorTypes.GraphQLSchema}: Attempting to remove undefined fields [${removedFields.join(', ')}] and retry query.`);

  const sanitizedDoc = visit(doc, {
    Field(node: FieldNode) {
      if (removedFields.includes(node.name.value)) {
        return null; // Remove the field
      }
      return node;
    },
  });

  return { sanitizedDoc, removedFields };
};

const createApiFetcher = <TResult,>(dependencies: {
  accountId: string;
  refreshAccessToken: AuthContextType['refreshAccessToken'];
  client: GraphQLClient;
  logout: ReturnType<typeof useAuth>['logout'];
  signal?: AbortSignal;
}) => {
  const { accountId, refreshAccessToken, client, logout, signal } = dependencies;

  const checkUnauthorized = (status: number) => status === 401;

  return async (uri: RequestDocument, variables?: Variables | (() => Variables)) => {
    const originalDocument = safeDocumentParse(uri);
    if (!originalDocument) {
      throw new Error(`${ErrorTypes.GraphQLSchema}: Invalid GraphQL document provided.`);
    }

    const executeRequest = async (document: DocumentNode, retryState: { auth?: boolean; sanitize?: boolean } = {}): Promise<any> => {
      const accessToken = getAccountTokenFromLS(accountId);
      const graphqlName = getOperationName(document);

      const coercedVariables = coerceVariablesWithTypes(
        typeof variables === 'function' ? variables() : variables || {},
        extractVariableTypes(document)
      );
      const shouldSendAccessToken = () => envVariables.VITE_SEND_REQUESTS_WITH_TOKEN === 'true';

      try {
        const res = await client.request<GraphQLResponse<TResult>>({
          document,
          variables: coercedVariables,
          signal,
          requestHeaders: {
            ...(shouldSendAccessToken() ? { Authorization: `Bearer ${accessToken}` } : {}),
            ...(graphqlName ? { graphqlName } : {}),
            'x-full-url': window.location.pathname,
            Accept: 'application/graphql-response+json',
          },
        });

        datadogRum.addAction(`Graphql:${graphqlName}`, { graphqlName, hasError: !!res.errors, request: print(document), response: res });

        if (res.errors) {
          throw new ClientError({ ...res } as any, { query: print(document) });
        }
        return res;
      } catch (error) {
        datadogRum.addAction(`Error:Graphql:${graphqlName}`, { graphqlName, hasError: true, request: print(document), response: error });

        // Sanitization retry logic for undefined fields
        if (!retryState.sanitize) {
          const sanitizationResult = sanitizeQueryForUndefinedFields(document, error as ClientError);
          if (sanitizationResult) {
            return executeRequest(sanitizationResult.sanitizedDoc, { ...retryState, sanitize: true });
          }
        }

        // Authentication retry logic
        if (checkUnauthorized(getNetworkErrorCode(error as ClientError)) && !retryState.auth) {
          try {
            await refreshAccessToken(accountId);
            return await executeRequest(document, { ...retryState, auth: true });
          } catch (refreshError) {
            console.error(`${ErrorTypes.Authentication}: Token refresh failed. Logging out.`, {
              accountId,
              graphqlName,
              refreshError,
            });
            await logout(!!getAccountTokenFromLS(accountId));
            throw refreshError;
          }
        }

        throw error;
      }
    };

    return executeRequest(originalDocument);
  };
};

export const useGqlQuery = <TQueryFnData = unknown, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>({
  query,
  variables,
  onSuccess,
  onError,
  select: userSelect,
  ...queryOptions
}: UseGqlQueryOptions<TQueryFnData, TData, TQueryKey>) => {
  const { accountId, refreshAccessToken, featureFlags, userIsActive, isLoading } = useAuthContext();
  const navigate = useNavigate();
  const { client } = use(GraphQLClientContext);
  const handleError = useHandleError(featureFlags?.[FeatureFlags.ShowServerErrorInToast]);
  const { logout } = useAuth();
  const document = useMemo(() => safeDocumentParse(query), [query]);

  const queryOptionsEnabled = queryOptions?.enabled;
  const isQueryEnabled = useMemo(
    () => isEnabled(userIsActive, accountId, queryOptionsEnabled, isLoading),
    [userIsActive, accountId, isLoading, queryOptionsEnabled]
  );
  const select = useMemo(() => {
    const getDataByOperationName = (response: TQueryFnData): TQueryFnData => {
      if (!response) {
        return response;
      }

      if (document) {
        const opDefs = document.definitions.filter(def => def.kind === 'OperationDefinition');
        if (opDefs.length === 1) {
          const opName = getOperationName(document);
          if (opName && response) {
            return response[opName];
          }
        }
      }
      return response;
    };

    if (userSelect) {
      return (response: TQueryFnData): TData => {
        const baseData = getDataByOperationName(response);
        return userSelect(baseData, response);
      };
    }

    const opDefs = document?.definitions.filter(def => def.kind === 'OperationDefinition');
    if (opDefs?.length === 1) {
      return (response: TQueryFnData) => getDataByOperationName(response) as unknown as TData;
    }

    return undefined;
  }, [document, userSelect]);

  const queryResult = useQuery<TQueryFnData, GraphQLError, TData, TQueryKey>({
    ...(queryOptions as UseQueryOptions<TQueryFnData, GraphQLError, TData, TQueryKey>),
    queryFn: async ({ signal }) => {
      try {
        const apiFetcher = createApiFetcher<TQueryFnData>({
          accountId,
          refreshAccessToken,
          client,
          logout,
          signal,
        });
        return await apiFetcher(query, variables);
      } catch (error) {
        throw convertToGraphQLError(error);
      }
    },
    enabled: isQueryEnabled,
    select,
    retry: (failureCount, error) => {
      if (failureCount >= 1) {
        return false;
      }
      const errorCode = getNetworkErrorCode(error);
      return errorCode !== 401 && errorCode !== 418;
    },
    retryDelay: attempt => 300 * 2 ** attempt,
  });

  const { data, error, isSuccess, isError, isRefetching } = queryResult;

  useEffect(() => {
    if (isSuccess && !isRefetching) {
      onSuccess?.(data);
    }
  }, [data, isSuccess, isRefetching]);

  useEffect(() => {
    if (isError && !isRefetching) {
      const isPermissionError = getNetworkErrorCode(error) === 418;

      if (isPermissionError) {
        const accountIdLocalStorage = localStorage.getItem(AuthStorageKeys.accountId);
        navigate(
          accountIdLocalStorage
            ? getPathNameByAccount(accountIdLocalStorage, getAppIdFromPathName(window.location.pathname) || APP_PATHS.PLATFORM)('not-found')
            : { to: 'not-found' }
        );
        return;
      }

      try {
        if (onError) {
          onError(error);
        } else {
          throw error;
        }
      } catch (e) {
        const graphqlName = document && getOperationName(document);
        handleError(e, { accountId, graphqlName });
      }
    }
  }, [error, isError, isRefetching]);

  return queryResult;
};

export const useAvQueries = <TData extends unknown[] = unknown[], TError extends unknown[] = unknown[]>({
  queries,
}: {
  queries: QueryObserverOptions<unknown>[];
}): UseQueryResult<TData[number], TError[number]>[] => {
  const queriesWithEnabled = useReturnQueriesData(queries);
  return useQueries({ queries: queriesWithEnabled });
};

export type UseAvMutationOptions<TData = unknown, TVariables = void, TContext = unknown> = Omit<
  MutationOptions<TData, GraphQLError, TVariables, TContext>,
  'mutationFn'
> & {
  mutation: RequestDocument;
  variablesAdapter?: (vars: TVariables) => Record<string, any>;
};

export const useGqlMutation = <TData = unknown, TVariables extends Variables = Variables, TContext = unknown>({
  mutation,
  variablesAdapter,
  ...mutationOptions
}: UseAvMutationOptions<TData, TVariables, TContext>): UseMutationResult<TData, GraphQLError, TVariables, TContext> => {
  const { accountId, refreshAccessToken, featureFlags } = useAuthContext();
  const navigate = useNavigate();
  const { client } = use(GraphQLClientContext);
  const handleError = useHandleError(featureFlags?.[FeatureFlags.ShowServerErrorInToast]);
  const { logout } = useAuth();

  const document = useMemo(() => safeDocumentParse(mutation), [mutation]);

  const mutationFn = async (variables: TVariables): Promise<TData> => {
    try {
      const apiFetcher = createApiFetcher({
        // eslint-disable-next-line no-underscore-dangle
        accountId: (variables as TVariables & { _accountId?: string })._accountId || accountId,
        refreshAccessToken,
        client,
        logout,
      });

      const adaptedVariables = variablesAdapter ? variablesAdapter(variables) : variables;
      const response: TData = await apiFetcher(mutation, adaptedVariables);

      if (!response) {
        return response;
      }

      if (document) {
        const opName = getOperationName(document);
        if (opName && response[opName] !== undefined) {
          return response[opName];
        }
      }
      return response as TData;
    } catch (error) {
      throw convertToGraphQLError(error);
    }
  };

  const { onError: onMutationError, ...restMutationOptions } = mutationOptions;

  return useMutation<TData, GraphQLError, TVariables, TContext>({
    ...restMutationOptions,
    mutationFn,
    onError: (error, vars, ctx) => {
      const isPermissionError = getNetworkErrorCode(error) === 418;

      if (isPermissionError) {
        const accountIdLocalStorage = localStorage.getItem(AuthStorageKeys.accountId);
        navigate(
          accountIdLocalStorage
            ? getPathNameByAccount(accountIdLocalStorage, getAppIdFromPathName(window.location.pathname) || APP_PATHS.PLATFORM)('not-found')
            : { to: 'not-found' }
        );
      }

      try {
        if (onMutationError) {
          onMutationError(error, vars, ctx);
        } else if (!isPermissionError) {
          throw error;
        }
      } catch (e) {
        const graphqlName = document && getOperationName(document);
        handleError(e, { accountId, graphqlName });
      }
    },
  });
};

export const useAvMutation = <TData = unknown, TError = unknown, TVariables = void>(options: MutationOptions<TData, TError, TVariables>) =>
  useMutation<TData, TError, TVariables>(options);

const useReturnQueriesData = (queries: any) => {
  const { accountId, userIsActive, isLoading } = useAuthContext();
  return queries.map(query => ({
    ...query,
    enabled: isEnabled(userIsActive, accountId, query, isLoading),
  }));
};

const isEnabled = (userIsActive, accountId, queryOptionsEnabled, isLoading) => {
  const isEnabledFromSystem = shouldSkipUILogin() || (userIsActive && !isLoading);
  return (queryOptionsEnabled ?? true) && isEnabledFromSystem;
};

interface UseGqlInfiniteQueryProps<TData> {
  queryKey: QueryKey;
  getNextPageParam?: (lastPage: TData, allPages: TData[]) => any;
  options?: any;
  queryFn?: QueryFunction;
}

export const useGqlInfiniteQuery = <TData = unknown,>({
  queryKey,
  queryFn,
  getNextPageParam,
  options = {},
}: UseGqlInfiniteQueryProps<TData>) => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, ...queryRest } = useInfiniteQuery<TData, unknown>({
    queryKey,
    queryFn,
    getNextPageParam: (lastPage, allPages) => (getNextPageParam ? getNextPageParam(lastPage, allPages) : undefined),
    ...options,
  });

  const handleObserver = useRef<IntersectionObserver | null>(null);

  const assignLastElementRef = useCallback(
    (element: HTMLElement | null) => {
      if (isLoading || !hasNextPage) {
        return;
      }

      if (handleObserver.current) {
        handleObserver.current.disconnect();
      }

      handleObserver.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && !isFetchingNextPage) {
          fetchNextPage();
        }
      });

      if (element) {
        handleObserver.current.observe(element);
      }
    },
    [isLoading, hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  return {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    assignLastElementRef,
    ...queryRest,
  };
};
